import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAmountInvestedAction, getSharesPurchasedAction } from "../actions";
import { SERIES_COLUM_TYPE, SERIES_LINE_CHART_TYPE } from "../constant/common";

import {
  amountInvestedSelector,
  amountInvestedStateSelectorWrapper,
  dividendDataSelector,
  shareDataSelector,
  sharePurchasedSelectorWrapper,
  updateQuery as updateQueryAction,
} from "../store/slice/trade";
import { calculateShareData, getDateTime, getNormalizedDataColumChart, getTotalReturnData } from "../utils";
import { useCompanySettings } from "./useCompanySettings";

/**
 *
 * @returns {[{
 *  amountInvested: import("../..").IMountShare,
 *  loading: boolean | undefined,
 *  error: object | null,
 *  fetchError: object | null,
 *  refreshError: object | null,
 *  refreshing: boolean | undefined,
 *  query: import("../..").IEntryMountShare
 * }, {
 *  getAmountInvested: (params: import("../..").IEntryMountShare) => void
 *  updateQuery: (params: import("../..").IEntryMountShare) => void
 * }]}
 */
export const useAmountInvested = () => {
  const amountInvestedStateSelector = useMemo(amountInvestedStateSelectorWrapper, []);
  const amountInvestedState = useSelector(amountInvestedStateSelector);
  const dispatch = useDispatch();

  const getAmountInvested = useCallback(
    /**
     *
     * @param {import("../..").IEntryMountShare} params
     * @returns
     */
    params => {
      dispatch(getAmountInvestedAction(params));
    },
    [dispatch]
  );

  const updateQuery = useCallback(
    params => {
      return dispatch(updateQueryAction(params));
    },
    [dispatch]
  );

  return [amountInvestedState, { getAmountInvested, updateQuery }];
};

/**
 *
 * @returns {[{
 *  sharePurchased: import("../..").IMountShare,
 *  loading: boolean | undefined,
 *  error: object | null,
 *  fetchError: object | null,
 *  refreshError: object | null,
 *  refreshing: boolean | undefined,
 *  query: import("../..").IEntryMountShare
 * }, {
 *  getSharesPurchased: (params: import("../..").IEntryMountShare) => void
 *  updateQuery: (params: import("../..").IEntryMountShare) => void
 * }]}
 */
export const useSharesPurchased = () => {
  const sharePurchasedSelector = useMemo(sharePurchasedSelectorWrapper, []);
  const sharePurchasedState = useSelector(sharePurchasedSelector);
  const dispatch = useDispatch();

  const getSharesPurchased = useCallback(
    /**
     *
     * @param {import("../..").IEntryMountShare} params
     * @returns
     */
    params => {
      dispatch(getSharesPurchasedAction(params));
    },
    [dispatch]
  );

  const updateQuery = useCallback(
    params => {
      return dispatch(updateQueryAction(params));
    },
    [dispatch]
  );

  return [sharePurchasedState, { getSharesPurchased, updateQuery }];
};

/**
 *
 * @returns {{
 * name?: string,
 * color?: string
 * }}
 */
export const useDividendData = () => {
  return useSelector(dividendDataSelector);
};

export const useLineChartData = () => {
  const shareData = useSelector(shareDataSelector);
  const dividendData = useSelector(dividendDataSelector);
  const amountPurchased = useSelector(amountInvestedSelector);

  const series = useMemo(() => {
    const shareDataNormalized = shareData.datas.map(({ date, close }) => [date, close]);
    const valueData = shareDataNormalized.map(d => ({
      date: d[0],
      value: d[1] * amountPurchased.shareBought,
    }));
    if (!shareDataNormalized || !shareDataNormalized.length) return null;
    if (!dividendData?.datas?.length) {
      return [
        {
          ...amountPurchased,
          instrumentId: amountPurchased.instrumentID,
          id: SERIES_LINE_CHART_TYPE.VALUE,
          datas: valueData,
        },
      ];
    }
    const dividendDataNormalized = dividendData.datas.map(({ date, close }) => [date, close]);
    const totalReturnData = getTotalReturnData(
      shareDataNormalized,
      dividendDataNormalized,
      amountPurchased.shareBought
    ).map(d => ({
      date: d[0],
      value: d[1],
    }));
    return [
      {
        ...amountPurchased,
        instrumentId: amountPurchased.instrumentID,
        datas: valueData,
        id: SERIES_LINE_CHART_TYPE.VALUE,
      },
      {
        ...amountPurchased,
        instrumentId: amountPurchased.instrumentID,
        datas: totalReturnData,
        id: SERIES_LINE_CHART_TYPE.TOTAL_RETURN,
      },
    ];
  }, [shareData, dividendData, amountPurchased]);

  return { series };
};

export const useColumnChartData = () => {
  const shareData = useSelector(shareDataSelector);
  const dividendData = useSelector(dividendDataSelector);
  const amountPurchased = useSelector(amountInvestedSelector);
  const settings = useCompanySettings();

  const result = useMemo(() => {
    const shareDataNormalized = shareData.datas.map(({ date, close }) => [date, close]);
    const sharePriceObj = shareDataNormalized.reduce((s, d) => ({ ...s, [d[0]]: d[1] }), {});
    if (!shareDataNormalized.length) return { data: [], xAxisCategory: [], dividendData: [] };
    if (!dividendData?.datas?.length) {
      const { seriesData, xAxisCategory, periodType } = calculateShareData(
        shareDataNormalized.map(s => [...s]),
        amountPurchased.shareBought
      );
      // fix data in first point
      seriesData[0][0] = shareDataNormalized[0][0];
      seriesData[0][1] = shareDataNormalized[0][1] * amountPurchased.shareBought;
      const { increase, decrease, offset } = getNormalizedDataColumChart(seriesData);
      return {
        xAxisCategory: xAxisCategory.map(cat => getDateTime(cat, settings)),
        periodType,
        sharePrice: seriesData.map(data => sharePriceObj[data[0]]),
        data: [increase, decrease, offset].map((d, i) => {
          let id = SERIES_COLUM_TYPE.OFFSET;
          switch (i) {
            case 0:
              id = SERIES_COLUM_TYPE.INCREASE;
              break;
            case 1:
              id = SERIES_COLUM_TYPE.DECREASE;
              break;
            default:
              break;
          }
          return {
            ...amountPurchased,
            datas: d,
            id,
          };
        }),
      };
    }
    const dividendDataNormalized = dividendData.datas.map(({ date, close }) => [date, close]);
    const totalReturns = getTotalReturnData(
      shareDataNormalized.map(s => [...s]),
      dividendDataNormalized,
      amountPurchased.shareBought
    );

    const dividendDataObj = totalReturns.reduce(
      (s, total, i) => ({
        ...s,
        [total[0]]: total[1] - shareDataNormalized[i][1] * amountPurchased.shareBought,
      }),
      {}
    );
    const { seriesData, xAxisCategory, periodType } = calculateShareData(totalReturns, 1);
    seriesData[0][0] = shareDataNormalized[0][0];
    seriesData[0][1] = shareDataNormalized[0][1] * amountPurchased.shareBought;
    const { increase, decrease, offset } = getNormalizedDataColumChart(seriesData);
    return {
      xAxisCategory: xAxisCategory.map(cat => getDateTime(cat, settings)),
      periodType,
      dividendData: seriesData.map(data => dividendDataObj[data[0]]),
      sharePrice: seriesData.map(data => sharePriceObj[data[0]]),
      data: [increase, decrease, offset].map((d, i) => {
        let id = SERIES_COLUM_TYPE.OFFSET;
        switch (i) {
          case 0:
            id = SERIES_COLUM_TYPE.INCREASE;
            break;
          case 1:
            id = SERIES_COLUM_TYPE.DECREASE;
            break;
          default:
            break;
        }
        return {
          ...amountPurchased,
          id,
          datas: d,
        };
      }),
    };
  }, [shareData, dividendData, amountPurchased]);

  return result;
};
